// ts-rest contract for the Relationship module (three-tier client model
// Stage 4, ADR-0015 D4/D7).
//
// One area covering the RelationshipsPanel reads (both directions merged,
// from the perspective of ?endpointId=), the desk/ops relationship mutations,
// the roles list feeding every role dropdown, and the admin-only catalogue
// management endpoints.
//
// DELIBERATE CLOSED-ENUM EXCEPTION (PRD §12 / ADR-0015 D4): `role` fields
// cross the wire as `z.string()`, never `z.enum`. Roles are a runtime-
// addable data catalogue — an admin adds "IFA" from the Administration page
// and uses it immediately, with no redeploy — so the wire cannot pin a
// closed vocabulary. The cost (no compile-time exhaustiveness on the one
// field where a typo has behavioural consequence) is bought back at the
// write boundary: unknown/inactive keys are refused (UNKNOWN_ROLE), every
// behavioural rule reads a catalogue flag, and the no-hardcoded-role guard
// forbids key literals outside the server's role-catalogue module.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { errorBodyWithCode as errorBody } from '../schemas/common';

const c = initContract();

// ---------------------------------------------------------------------------
// Enums (closed domain values — snake_case VALUES, camelCase keys)
// ---------------------------------------------------------------------------

/**
 * Endpoint types are Object Type KEYS — the kebab-case identifiers of the
 * three tiers, pinned verbatim (structural registry identifiers, not a new
 * enum vocabulary; the snake_case-values convention governs business
 * vocabularies like `appliesTo` below).
 */
export const relationshipEndpointTypeSchema = z.enum([
    'contact',
    'suitability-record',
    'account',
    'client',
]);
export type RelationshipEndpointType = z.infer<typeof relationshipEndpointTypeSchema>;

// The pair kinds name the target tiers directly. `contact_client` means a
// Contact linked to a Client.
export const relationshipAppliesToSchema = z.enum([
    'contact_suitability',
    'contact_account',
    'contact_contact',
    'suitability_account',
    'contact_client',
]);
export type RelationshipAppliesTo = z.infer<typeof relationshipAppliesToSchema>;

export const relationshipAgeBandSchema = z.enum(['minor', 'adult']);
export type RelationshipAgeBand = z.infer<typeof relationshipAgeBandSchema>;

/**
 * The relationship kind of an unordered endpoint-type pair, or null when the
 * pair is unsupported. Mirrors the ontology's canonical-order helper for
 * UI-side role filtering (the web layer cannot import the ontology runtime
 * — contract-boundary guard).
 */
export function relationshipPairKind(
    a: RelationshipEndpointType,
    b: RelationshipEndpointType,
): RelationshipAppliesTo | null {
    // The comparison is unordered and it does NOT sort the two keys. A sorted
    // join once made the result depend on the alphabetical order of the Object
    // Type keys, so one rename moved a key in front of `contact` and every pair
    // fell through to null.
    const pair = (x: RelationshipEndpointType, y: RelationshipEndpointType): boolean =>
        (a === x && b === y) || (a === y && b === x);
    if (pair('contact', 'suitability-record')) return 'contact_suitability';
    if (pair('contact', 'account')) return 'contact_account';
    if (pair('contact', 'contact')) return 'contact_contact';
    if (pair('suitability-record', 'account')) return 'suitability_account';
    if (pair('contact', 'client')) return 'contact_client';
    return null;
}

/** Tier labels, in one place so the per-record panel and the book-level
 *  directory can never disagree about what an endpoint type is called. */
export const RELATIONSHIP_ENDPOINT_TYPE_LABELS: Record<RelationshipEndpointType, string> = {
    contact: 'Contact',
    'suitability-record': 'Suitability record',
    // THE TWO TIERS TRADED THE WORD, on the approval of the product owner. The
    // `client` tier IS the registered holder, so it wears "Client", and the
    // `account` tier keeps "Account group". The picker offers both tiers in one
    // list, so one shared word would have named two things.
    account: 'Account group',
    client: 'Client',
};

/**
 * SINGLE-ROLE KINDS. For every kind whose first end is a Contact, the stored
 * role describes the CONTACT's role on the record / account group / client; the
 * other end mirrors the same key by storage convention and must never be
 * rendered (a suitability record must not describe itself as "Sole Owner" — see
 * `vendor/septima-ontology-wealth/packages/ontology-wealth/specs/client/relationship.md`).
 *
 * This is a comparison of relationship KINDS, not of role keys — the
 * no-hardcoded-role guard is about the latter.
 */
export function isSingleRoleRelationshipKind(kind: RelationshipAppliesTo): boolean {
    return (
        kind === 'contact_suitability' || kind === 'contact_account' || kind === 'contact_client'
    );
}

// ---------------------------------------------------------------------------
// Roles
// ---------------------------------------------------------------------------

export const relationshipRoleSchema = z.object({
    roleId: z.string(),
    /** Catalogue key — a plain string BY DESIGN (see header comment). */
    key: z.string(),
    label: z.string(),
    appliesTo: relationshipAppliesToSchema,
    displayPriority: z.union([z.literal(1), z.literal(2), z.literal(3)]).nullable(),
    nominatedRepresentative: z.boolean(),
    ageBand: relationshipAgeBandSchema.nullable(),
    pairKey: z.string().nullable(),
    sortOrder: z.number().int(),
    active: z.boolean(),
});
export type RelationshipRoleDto = z.infer<typeof relationshipRoleSchema>;

export const listRelationshipRolesResponseSchema = z.object({
    roles: z.array(relationshipRoleSchema),
});
export type ListRelationshipRolesResponse = z.infer<typeof listRelationshipRolesResponseSchema>;

/**
 * The role at one end of a relationship, as every relationship surface renders
 * it — and as an editor must write it back.
 *
 * `key` and `label` are the role AS DISPLAYED. For an age-banded role they are
 * DERIVED on read from the role-carrying contact's date of birth (Child (Minor)
 * becomes Child (Adult) on the eighteenth birthday, with no scheduled job), so
 * they can differ from what the row stores. That divergence is by design —
 * `vendor/septima-ontology-wealth/packages/ontology-wealth/specs/client/relationship.md`.
 *
 * `storedKey` is what the row actually holds. An edit form seeds from it, never
 * from `key` (SEP-214): a reader who corrects only the note must not silently
 * settle an age-banded role into the derived value, which would write a role
 * change into the audit trail that nobody made. The two are identical for every
 * role the catalogue does not age-band.
 */
export const relationshipEndRoleSchema = z.object({
    key: z.string(),
    label: z.string(),
    storedKey: z.string(),
});
export type RelationshipEndRole = z.infer<typeof relationshipEndRoleSchema>;

// ---------------------------------------------------------------------------
// Panel rows — both directions merged, from ?endpointId='s perspective
// ---------------------------------------------------------------------------

/**
 * The `relationshipId` of a DERIVED row (SEP-188): the two legs it was inferred
 * from, sorted so the key is stable whichever end asked. The `derived:` prefix
 * makes the id impossible to mistake for a stored uuid, and impossible to post
 * back to an end / re-role endpoint — there is no such relationship row.
 */
export function derivedRelationshipId(legAId: string, legBId: string): string {
    return `derived:${[legAId, legBId].sort().join('+')}`;
}

/**
 * The intermediary a DERIVED row runs through (SEP-188). Today that is always
 * a suitability record: a contact reaches the client its record owns, and the
 * client reaches the contact, without either link being recorded.
 */
export const relationshipViaSchema = z.object({
    endpointType: relationshipEndpointTypeSchema,
    endpointId: z.string(),
    /** The intermediary named on ONE line, the human first (`Pryce ·
     *  SUR-1001`) — this marker reads as a sentence and has no second line to
     *  stack a reference on. Null when its row sits outside the caller's
     *  read. */
    name: z.string().nullable(),
});
export type RelationshipVia = z.infer<typeof relationshipViaSchema>;

export const relationshipRowSchema = z.object({
    /** A stored relationship uuid, OR — when {@link relationshipRowSchema.shape.via}
     *  is non-null — the synthetic `derived:` composite of the two legs the row
     *  was inferred from ({@link derivedRelationshipId}). */
    relationshipId: z.string(),
    kind: relationshipAppliesToSchema,
    /**
     * Which STORED end the queried endpoint is: true when it is endpoint 1 (SEP-214).
     *
     * The rest of this row is written from the endpoint's perspective, which is
     * what a reader wants and what an editor cannot use — `update-relationship`
     * takes `endpoint1Role` / `endpoint2Role` in canonical stored order. Without this
     * flag the panel would have to guess the orientation, and would guess wrong
     * for exactly the contact↔contact links whose two roles differ.
     *
     * NULL on a DERIVED row (`via !== null`): an inferred link has no stored row
     * and therefore no canonical order at all. That is the same reason it
     * carries no End and no Edit affordance.
     */
    ownIsEndpoint1: z.boolean().nullable(),
    /** The queried endpoint's own end. */
    ownRole: relationshipEndRoleSchema,
    /** The other end — one click to any of them (PRD §10). */
    counterpartType: relationshipEndpointTypeSchema,
    counterpartId: z.string(),
    /** Display name resolved server-side — the HUMAN on every tier (see
     * {@link relationshipEndpointSchema} for the per-tier formats); null when the
     * row is outside the caller's read. */
    counterpartName: z.string().nullable(),
    /**
     * The counterpart's own reference, on the same rule as
     * {@link relationshipEndpointSchema.shape.reference}: set only when
     * {@link relationshipRowSchema.shape.counterpartName} carries a human name,
     * so the panel can print `Client · 2620 140229` or
     * `Suitability record · SUR-6353` beneath it without ever repeating a bare
     * reference.
     */
    counterpartReference: z.string().nullable(),
    /**
     * The person `Client` an `account` counterpart resolves to — what a
     * click on the Client endpoint opens (SEP-188). Null on every other tier, and
     * null for a client whose Account has no single owner or whose Client row
     * sits outside the caller's read. See {@link relationshipEndpointSchema}.
     */
    counterpartClientId: z.string().nullable(),
    /** Counterpart-end role. The label is DERIVED server-side for
     * age-banded roles (Child Minor/Adult) from the contact's date of
     * birth against clockNow() — the stored key is not authoritative, and
     * {@link relationshipEndRoleSchema.shape.storedKey} carries what is. */
    counterpartRole: relationshipEndRoleSchema,
    startDate: z.string().nullable(),
    /** Ended links stay visible, greyed, with their end date. Ending never
     * hides a link. A person can delete an ended link (SEP-244.4), and the
     * delete keeps no copy. */
    endDate: z.string().nullable(),
    createdAt: z.string(),
    note: z.string().nullable(),
    /**
     * Null on a STORED relationship. Non-null on a row DERIVED one hop through
     * a suitability record (SEP-188) — the intermediary the link runs through,
     * so a reader can tell an inferred link from a recorded one. `via !== null`
     * is the ONE representation of "derived"; there is no second boolean.
     *
     * A derived row has no relationship row behind it: it can be neither ended
     * nor re-roled, and it never reaches the book-level register, which is a
     * list of what is RECORDED.
     */
    via: relationshipViaSchema.nullable(),
});
export type RelationshipRow = z.infer<typeof relationshipRowSchema>;

export const listRelationshipsResponseSchema = z.object({
    rows: z.array(relationshipRowSchema),
});
export type ListRelationshipsResponse = z.infer<typeof listRelationshipsResponseSchema>;

// ---------------------------------------------------------------------------
// Book-level rows (SEP-176) — the whole book at once, in STORED order
// ---------------------------------------------------------------------------

/**
 * One end of a relationship, as the book-level directory renders it.
 *
 * Unlike {@link relationshipRowSchema}, which is written from ONE endpoint's
 * perspective (`ownRole` / `counterpart*`), this row has no perspective: there
 * is no "current record" on a book-level list. Party 1 and Party 2 are the
 * STORED canonical order (contact → suitability-record, contact →
 * account, suitability-record → account, contact ↔ contact
 * sorted) — which is precisely what makes an endpoint-type filter on each side
 * meaningful, and what the Titan Party 1 / Role 1 / Role 2 / Party 2 layout
 * has always shown.
 */
export const relationshipEndpointSchema = z.object({
    endpointType: relationshipEndpointTypeSchema,
    endpointId: z.string(),
    /**
     * The endpoint's display label, resolved server-side within the CALLER's read
     * scope (ADR-0005) and null when the row is outside it — the id stays so
     * the row still renders, the name is withheld. One format per tier:
     *
     *   - contact — the full display name, e.g. `Lucinda Pryce`;
     *   - suitability record — `Pryce`: the same human anchor the SEP-180
     *     picker label uses (primary contact's surname, else their full name,
     *     else the entity name), degrading to a bare `SUR-1001` when nothing
     *     resolves;
     *   - client — `Burgess, Mr Alistair Harvey Innes`: the person Client the
     *     Account belongs to, degrading to the bare account reference
     *     when no single readable client resolves (SEP-188).
     *
     * The human LEADS on every tier. A reference names nobody, so both
     * reference-carrying tiers moved theirs to
     * {@link relationshipEndpointSchema.shape.reference} (SEP-188).
     */
    label: z.string().nullable(),
    /**
     * The endpoint's own reference — a suitability record's `SUR-1001`, or an
     * Account's parent code falling back to its raw client reference.
     * Set on the two reference-carrying tiers, and only when
     * {@link relationshipEndpointSchema.shape.label} carries a human name: one
     * person can hold several records, so without it their rows read
     * identically (SEP-188). It is null on a Contact, and null when the label
     * already IS the bare reference, so a cell never prints it twice.
     *
     * The renderer puts it under the name, beside the tier word
     * (`Client · 2620 140229`) — the name identifies the endpoint, the reference
     * only separates two of them.
     */
    reference: z.string().nullable(),
    /**
     * Where a click on an `account` endpoint goes (SEP-188): the person
     * `Client` the Account resolves to, which is the record its
     * identity is actually read on — the Clients tier has no detail page of its
     * own. Null on every other tier, and null whenever `label` also lost its
     * name: the Account resolved to no single person (`Account.clientId`
     * — SEP-166), or that person sat outside the CALLER's read (ADR-0005). Name
     * and link come from ONE scoped read, so an endpoint is never named without
     * being openable.
     */
    clientId: z.string().nullable(),
    /**
     * The role at THIS end, or null when the tier convention hides it — see
     * {@link isSingleRoleRelationshipKind}. Resolved against the
     * `relationship_roles` catalogue (never a hard-coded label), with the
     * age-banded Child (Minor)/(Adult) label derived on read (ADR-0015 D7).
     */
    role: relationshipEndRoleSchema.nullable(),
});
export type RelationshipEndpoint = z.infer<typeof relationshipEndpointSchema>;

export const bookRelationshipRowSchema = z.object({
    relationshipId: z.string(),
    kind: relationshipAppliesToSchema,
    endpoint1: relationshipEndpointSchema,
    endpoint2: relationshipEndpointSchema,
    startDate: z.string().nullable(),
    /** Ended links stay on the wire and stay on the page — greyed, with their
     * end date. Nothing is ever hard deleted (ADR-0015 D7). */
    endDate: z.string().nullable(),
    createdAt: z.string(),
    note: z.string().nullable(),
});
export type BookRelationshipRow = z.infer<typeof bookRelationshipRowSchema>;

/** Counts over the caller's WHOLE readable set — the page's endpoint-type filters
 *  are applied in the browser, so the header cards do not move when one does. */
export const bookRelationshipsStatsSchema = z.object({
    total: z.number().int(),
    active: z.number().int(),
    ended: z.number().int(),
});
export type BookRelationshipsStats = z.infer<typeof bookRelationshipsStatsSchema>;

export const listBookRelationshipsResponseSchema = z.object({
    rows: z.array(bookRelationshipRowSchema),
    stats: bookRelationshipsStatsSchema,
});
export type ListBookRelationshipsResponse = z.infer<typeof listBookRelationshipsResponseSchema>;

// ---------------------------------------------------------------------------
// Mutation requests / responses
// ---------------------------------------------------------------------------

export const createRelationshipRequestSchema = z.object({
    endpoint1Type: relationshipEndpointTypeSchema,
    endpoint1Id: z.string().min(1),
    /** Role key for endpoint 1's end — plain string by design (header note). */
    endpoint1Role: z.string().min(1),
    endpoint2Type: relationshipEndpointTypeSchema,
    endpoint2Id: z.string().min(1),
    endpoint2Role: z.string().min(1),
    /** Calendar date — matches the create-relationship Action input, so a
     * malformed date refuses at the contract with a proper message. */
    startDate: z.string().date().optional(),
    note: z.string().optional(),
});
export type CreateRelationshipRequest = z.infer<typeof createRelationshipRequestSchema>;

/** Create/update responses carry warn-never-block notices (PRD §9.3 —
 * a second Nominated Representative warns; the UI renders them amber). */
export const relationshipMutationResponseSchema = z.object({
    ok: z.boolean(),
    relationshipId: z.string(),
    warnings: z.array(z.string()),
});
export type RelationshipMutationResponse = z.infer<typeof relationshipMutationResponseSchema>;

/**
 * Correct a relationship in place (SEP-214) — every field
 * {@link createRelationshipRequestSchema} collects EXCEPT the endpoints.
 *
 * The endpoints are absent BY DESIGN, not by omission: a different pairing is a
 * different relationship, and the ownership unique index and the same-owner
 * rule are both built on the stored pair. The edit surface renders them as
 * read-only text for exactly that reason.
 *
 * `startDate` and `note` are patch fields — absent leaves the stored value,
 * `null` clears it. The two must stay distinguishable, so neither may collapse
 * to a bare `.optional()`.
 */
export const updateRelationshipRequestSchema = z.object({
    endpoint1Role: z.string().min(1),
    endpoint2Role: z.string().min(1),
    /** Calendar date — matches the Action input, so a malformed date refuses
     * at the contract with a proper message. Null clears the stored date. */
    startDate: z.string().date().nullable().optional(),
    note: z.string().nullable().optional(),
});
export type UpdateRelationshipRequest = z.infer<typeof updateRelationshipRequestSchema>;

// ---------------------------------------------------------------------------
// Complete the client links of one Account (SEP-300)
// ---------------------------------------------------------------------------

/**
 * What to do with an existing link that a new link replaces. The user makes
 * this choice explicitly, and the server never makes it for them.
 *
 *   - `end` keeps the old link as history, with the end date of today;
 *   - `delete` removes a link that was wrong, and keeps no copy (SEP-244.4).
 */
export const clientLinkReplacementModeSchema = z.enum(['end', 'delete']);
export type ClientLinkReplacementMode = z.infer<typeof clientLinkReplacementModeSchema>;

export const clientLinkReplacementSchema = z.object({
    /** The stored id of the active link to replace. */
    relationshipId: z.string().uuid(),
    mode: clientLinkReplacementModeSchema,
});
export type ClientLinkReplacement = z.infer<typeof clientLinkReplacementSchema>;

/**
 * The links of ONE Account, completed as ONE operation (SEP-300). Each part is
 * optional, and an absent part keeps what is recorded now. The server runs each
 * part through the existing Relationship Actions in one database transaction.
 * When one part fails, no part is saved.
 *
 * The role keys are catalogue keys, as on {@link createRelationshipRequestSchema}.
 */
export const completeClientLinksRequestSchema = z
    .object({
        accountId: z.string().min(1),
        /** The Suitability Record that owns the Account (`suitability_account`). */
        record: z
            .object({
                suitabilityRecordId: z.string().min(1),
                role: z.string().min(1),
                replace: clientLinkReplacementSchema.optional(),
            })
            .optional(),
        /** A Contact on the owning record (`contact_suitability`). The owning
         *  record is `record` when it is given, else the record that owns the
         *  Account now. */
        contact: z
            .object({
                contactId: z.string().min(1),
                role: z.string().min(1),
                replace: clientLinkReplacementSchema.optional(),
            })
            .optional(),
        /** A direct Contact of the person Client of the Account
         *  (`contact_client`). The user asks for it explicitly. */
        clientAnchor: z
            .object({
                clientId: z.string().min(1),
                contactId: z.string().min(1),
                role: z.string().min(1),
                replace: clientLinkReplacementSchema.optional(),
            })
            .optional(),
    })
    .refine((body) => body.record || body.contact || body.clientAnchor, {
        message: 'NOTHING_TO_CHANGE: give at least one link',
    });
export type CompleteClientLinksRequest = z.infer<typeof completeClientLinksRequestSchema>;

export const clientLinkStepSchema = z.enum(['record', 'contact', 'client_anchor']);
export type ClientLinkStep = z.infer<typeof clientLinkStepSchema>;

export const clientLinkOutcomeSchema = z.object({
    step: clientLinkStepSchema,
    /** `unchanged` when the same active link was already recorded. */
    result: z.enum(['created', 'unchanged']),
    /** The active link of this step after the operation. */
    relationshipId: z.string(),
    /** The link that this step ended or deleted, or null. */
    replaced: clientLinkReplacementSchema.nullable(),
});
export type ClientLinkOutcome = z.infer<typeof clientLinkOutcomeSchema>;

export const completeClientLinksResponseSchema = z.object({
    ok: z.boolean(),
    outcomes: z.array(clientLinkOutcomeSchema),
    /** Warn-never-block notices of the create Action (PRD §9.3). */
    warnings: z.array(z.string()),
});
export type CompleteClientLinksResponse = z.infer<typeof completeClientLinksResponseSchema>;

// ---------------------------------------------------------------------------
// Admin role-catalogue requests
// ---------------------------------------------------------------------------

export const createRelationshipRoleRequestSchema = z.object({
    key: z.string().regex(/^[a-z][a-z0-9_]{1,63}$/),
    label: z.string().min(1),
    appliesTo: relationshipAppliesToSchema,
    displayPriority: z
        .union([z.literal(1), z.literal(2), z.literal(3)])
        .nullable()
        .optional(),
    nominatedRepresentative: z.boolean().optional(),
    ageBand: relationshipAgeBandSchema.nullable().optional(),
    pairKey: z.string().nullable().optional(),
    sortOrder: z.number().int().optional(),
});
export type CreateRelationshipRoleRequest = z.infer<typeof createRelationshipRoleRequestSchema>;

export const updateRelationshipRoleRequestSchema = z.object({
    label: z.string().min(1).optional(),
    sortOrder: z.number().int().optional(),
    /** Reactivation only — deactivation has its own guarded endpoint. */
    active: z.literal(true).optional(),
});
export type UpdateRelationshipRoleRequest = z.infer<typeof updateRelationshipRoleRequestSchema>;

const okRole = z.object({ ok: z.boolean(), roleId: z.string() });
const okRelationship = z.object({ ok: z.boolean(), relationshipId: z.string() });

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const relationshipsContract = c.router(
    {
        listRoles: {
            method: 'GET',
            path: '/relationships/roles',
            responses: { 200: listRelationshipRolesResponseSchema },
            summary:
                'The role catalogue — feeds every role dropdown (active) and the admin card (all)',
        },
        listBookRelationships: {
            method: 'GET',
            path: '/relationships/book',
            // NO QUERY. The caller's whole readable set comes back unpaged, so
            // a server-side endpoint-type filter would buy nothing and cost the
            // page its stability: the filters are react-query key material, so
            // every change would mint an uncached key and blank the header
            // cards for a round trip. The page filters in the browser.
            responses: { 200: listBookRelationshipsResponseSchema },
            summary:
                "Every relationship in the caller's book, Party 1 / Role 1 / Role 2 / Party 2 (SEP-176)",
        },
        listRelationships: {
            method: 'GET',
            path: '/relationships',
            query: z.object({ endpointId: z.string().min(1) }),
            responses: { 200: listRelationshipsResponseSchema },
            summary: 'Every relationship an endpoint carries, both directions merged (PRD §10)',
        },
        createRelationship: {
            method: 'POST',
            path: '/relationships',
            body: createRelationshipRequestSchema,
            responses: { 200: relationshipMutationResponseSchema, 400: errorBody, 404: errorBody },
            summary:
                'Create a relationship (create-relationship Action — same-owner, canonical order, ownership uniqueness)',
        },
        updateRelationship: {
            method: 'PATCH',
            path: '/relationships/:relationshipId',
            body: updateRelationshipRequestSchema,
            responses: { 200: relationshipMutationResponseSchema, 400: errorBody, 404: errorBody },
            summary:
                'Correct a relationship — both roles, start date and note; endpoints never change (update-relationship Action)',
        },
        endRelationship: {
            method: 'POST',
            path: '/relationships/:relationshipId/end',
            body: z.object({}).optional(),
            responses: { 200: okRelationship, 400: errorBody, 404: errorBody },
            summary: 'End a relationship — sets endDate, never deletes (end-relationship Action)',
        },
        deleteRelationship: {
            method: 'DELETE',
            path: '/relationships/:relationshipId',
            body: z.object({}).optional(),
            responses: { 200: okRelationship, 400: errorBody, 404: errorBody },
            summary:
                'Delete a relationship (delete-relationship Action, SEP-244). The delete is permanent: it removes the row and keeps no recoverable copy. The audit ledger records who deleted it and what it said. Use `endRelationship` above to keep the history.',
        },
        completeClientLinks: {
            method: 'POST',
            path: '/relationships/client-links',
            body: completeClientLinksRequestSchema,
            responses: {
                200: completeClientLinksResponseSchema,
                400: errorBody,
                404: errorBody,
            },
            summary:
                'Complete the links of one Account in one transaction: the owning Suitability Record, a Contact on it, and a direct Client contact (create-, end- and delete-relationship Actions, SEP-300)',
        },
        createRole: {
            method: 'POST',
            path: '/relationship-roles',
            body: createRelationshipRoleRequestSchema,
            responses: { 200: okRole, 400: errorBody },
            summary: 'Admin: add a role to the catalogue (create-relationship-role Action)',
        },
        updateRole: {
            method: 'PATCH',
            path: '/relationship-roles/:roleId',
            body: updateRelationshipRoleRequestSchema,
            responses: { 200: okRole, 400: errorBody, 404: errorBody },
            summary:
                'Admin: edit a role label / sort order, or reactivate (update-relationship-role Action)',
        },
        deactivateRole: {
            method: 'POST',
            path: '/relationship-roles/:roleId/deactivate',
            body: z.object({}).optional(),
            responses: { 200: okRole, 400: errorBody, 404: errorBody },
            summary:
                'Admin: deactivate a role — refused while a display-priority role is in use (deactivate-relationship-role Action)',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
